<app-menu>
  <div class="pm-shell">
    <header class="pm-header">
      <div>
        <p class="pm-kicker">Operations</p>
        <h1>Pool Manager</h1>
        <p class="pm-subtitle">Pools, clients, stores, distribution centers, and drop trailer carriers.</p>
      </div>

      <div class="pm-header-actions">
        <button mat-stroked-button color="primary" (click)="refresh()">
          <mat-icon>refresh</mat-icon>
          Refresh
        </button>
        <button mat-raised-button color="primary" (click)="startNewPool()">
          <mat-icon>add_location_alt</mat-icon>
          Add Pool
        </button>
      </div>
    </header>

    <mat-progress-bar
      *ngIf="loadingPools || loadingDetail || loadingDistributionCenters || loadingCarriers"
      mode="indeterminate"
    ></mat-progress-bar>

    <section class="pm-metrics">
      <div>
        <span>{{ pools.length }}</span>
        <p>Pool points</p>
      </div>
      <div>
        <span>{{ clients.length }}</span>
        <p>Selected clients</p>
      </div>
      <div>
        <span>{{ totalStores }}</span>
        <p>Store assignments</p>
      </div>
      <div>
        <span>{{ distributionCenters.length }}</span>
        <p>Distribution centers</p>
      </div>
      <div>
        <span>{{ carriers.length }}</span>
        <p>Drop trailer carriers</p>
      </div>
    </section>

    <div class="pm-alert" *ngIf="loadError">
      {{ loadError }}
    </div>

    <mat-tab-group class="pm-tab-shell mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" animationDuration="150ms">
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="pm-tab-label">
            <span class="pm-tab-icon bg-blue-50 text-blue-700">
              <mat-icon>hub</mat-icon>
            </span>
            <span class="pm-tab-text">
              <strong>Pool Assignments</strong>
              <small>{{ pools.length }} pools | {{ totalStores }} stores</small>
            </span>
          </span>
        </ng-template>
        <main class="grid gap-4 p-4 xl:grid-cols-[360px_minmax(0,1fr)]">
          <section class="rounded-lg border border-slate-200 bg-white p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 class="m-0 text-lg font-bold text-slate-900">Pool Points</h2>
                <p class="m-0 mt-1 text-sm text-slate-500">{{ filteredPools.length }} shown</p>
              </div>
              <mat-icon class="text-teal-700">pool</mat-icon>
            </div>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Search pools</mat-label>
              <input matInput [(ngModel)]="poolSearch" />
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>

            <div class="flex max-h-[calc(100vh-330px)] flex-col gap-2 overflow-auto pr-1">
              <button
                type="button"
                class="pm-list-item"
                *ngFor="let pool of filteredPools; trackBy: trackPool"
                [class.pm-list-item-active]="selectedPool?.pool_point_uuid === pool.pool_point_uuid && poolMode === 'edit'"
                (click)="selectPool(pool)"
              >
                <span class="pm-title">{{ poolLabel(pool) }}</span>
                <span class="pm-meta">{{ addressForPool(pool) }}</span>
                <span class="pm-counts">
                  <span>{{ count(pool.client_count) }} clients</span>
                  <span>{{ count(pool.store_count) }} stores</span>
                </span>
              </button>

              <div class="pm-empty" *ngIf="!loadingPools && filteredPools.length === 0">
                No pool points.
              </div>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4" *ngIf="poolMode === 'create' || selectedPool; else noPoolSelected">
            <div class="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 class="m-0 text-xl font-bold text-slate-900">{{ poolMode === 'create' ? 'New Pool Point' : poolLabel(selectedPool) }}</h2>
                <p class="m-0 mt-1 text-sm text-slate-500">{{ poolMode === 'create' ? 'Draft' : addressForPool(selectedPool) }}</p>
              </div>
              <div class="pm-icon-actions">
                <button mat-icon-button matTooltip="Open pool contact" (click)="openContact(selectedPool?.pool_point_contact_uuid)">
                  <mat-icon>contact_page</mat-icon>
                </button>
                <button mat-icon-button color="warn" matTooltip="Delete pool" [disabled]="poolMode === 'create'" (click)="deletePool()">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </div>

            <form [formGroup]="poolForm" class="pm-form">
              <div class="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                <mat-form-field appearance="outline">
                  <mat-label>Pool name</mat-label>
                  <input matInput formControlName="pool_point_name" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Pool contact</mat-label>
                  <input matInput [formControl]="poolContactSearch" [matAutocomplete]="poolContactAuto" />
                  <mat-icon matSuffix>person_search</mat-icon>
                  <mat-autocomplete #poolContactAuto="matAutocomplete" [displayWith]="displayContactOption" (optionSelected)="onPoolContactSelected($event.option.value)">
                    <mat-option *ngFor="let contact of poolContactOptions$ | async" [value]="contact">
                      <span>{{ contactLabel(contact) }}</span>
                      <small>{{ contactMeta(contact) }}</small>
                    </mat-option>
                  </mat-autocomplete>
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Pool contact UUID</mat-label>
                  <input matInput formControlName="pool_point_contact_uuid" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Pool address</mat-label>
                  <mat-select formControlName="pool_point_address_uuid">
                    <mat-option value="">No address</mat-option>
                    <mat-option *ngFor="let address of poolAddresses; trackBy: trackAddress" [value]="address.contact_address_uuid">
                      {{ addressLabel(address) }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="pm-actions-row">
                <button mat-raised-button color="primary" type="button" (click)="savePool()" [disabled]="savingPool">
                  <mat-icon>save</mat-icon>
                  {{ poolMode === 'create' ? 'Create Pool' : 'Save Pool' }}
                </button>
                <button mat-stroked-button type="button" (click)="startNewPool()">
                  <mat-icon>restart_alt</mat-icon>
                  Clear
                </button>
              </div>
            </form>

            <div class="mt-5 grid gap-4 2xl:grid-cols-[420px_minmax(0,1fr)]">
              <section class="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div class="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 class="m-0 text-base font-bold text-slate-900">Clients</h3>
                    <p class="m-0 mt-1 text-sm text-slate-500">{{ filteredClients.length }} shown</p>
                  </div>
                  <div class="pm-icon-actions">
                    <button mat-icon-button color="primary" matTooltip="Add client" [disabled]="!selectedPool" (click)="startNewClient()">
                      <mat-icon>person_add</mat-icon>
                    </button>
                    <button mat-icon-button color="warn" matTooltip="Delete selected client" [disabled]="!selectedClient" (click)="deleteClient()">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                </div>

                <mat-form-field appearance="outline" class="w-full">
                  <mat-label>Search clients</mat-label>
                  <input matInput [(ngModel)]="clientSearch" />
                  <mat-icon matSuffix>search</mat-icon>
                </mat-form-field>

                <div class="mb-4 flex max-h-72 flex-col gap-2 overflow-auto pr-1">
                  <button
                    type="button"
                    class="pm-list-item"
                    *ngFor="let client of filteredClients; trackBy: trackClient"
                    [class.pm-list-item-active]="selectedClient?.pool_point_client_uuid === client.pool_point_client_uuid"
                    (click)="selectClient(client)"
                  >
                    <span class="pm-title">{{ clientLabel(client) }}</span>
                    <span class="pm-meta">
                      {{ client.pool_point_client_id ? 'Pool ID ' + client.pool_point_client_id : 'No pool ID' }}
                      <span *ngIf="client.pool_point_client_scope"> | Scope {{ client.pool_point_client_scope }}</span>
                    </span>
                    <span class="pm-counts">
                      <span>{{ (storesByClient.get(client.pool_point_client_uuid) || []).length }} stores</span>
                      <span>{{ count(client.distribution_center_count) }} DCs</span>
                    </span>
                  </button>

                  <div class="pm-empty" *ngIf="!loadingDetail && filteredClients.length === 0">
                    No clients.
                  </div>
                </div>

                <form [formGroup]="clientForm" class="pm-subform">
                  <h4>{{ clientMode === 'create' ? 'New Client' : 'Client Details' }}</h4>
                  <mat-form-field appearance="outline">
                    <mat-label>Client name</mat-label>
                    <input matInput formControlName="pool_point_client_name" />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Client contact</mat-label>
                    <input matInput [formControl]="clientContactSearch" [matAutocomplete]="clientContactAuto" />
                    <mat-icon matSuffix>person_search</mat-icon>
                    <mat-autocomplete #clientContactAuto="matAutocomplete" [displayWith]="displayContactOption" (optionSelected)="onClientContactSelected($event.option.value)">
                      <mat-option *ngFor="let contact of clientContactOptions$ | async" [value]="contact">
                        <span>{{ contactLabel(contact) }}</span>
                        <small>{{ contactMeta(contact) }}</small>
                      </mat-option>
                    </mat-autocomplete>
                  </mat-form-field>
                  <div class="grid gap-3 md:grid-cols-2">
                    <mat-form-field appearance="outline">
                      <mat-label>Pool ID</mat-label>
                      <input matInput formControlName="pool_point_client_id" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Client Scope</mat-label>
                      <input matInput formControlName="pool_point_client_scope" />
                    </mat-form-field>
                  </div>
                  <mat-form-field appearance="outline">
                    <mat-label>Customer contact UUID</mat-label>
                    <input matInput formControlName="pool_point_client_contact_uuid" />
                  </mat-form-field>
                  <div class="pm-actions-row">
                    <button mat-raised-button color="primary" type="button" (click)="saveClient()" [disabled]="!selectedPool || savingClient">
                      <mat-icon>save</mat-icon>
                      {{ clientMode === 'create' ? 'Add Client' : 'Save Client' }}
                    </button>
                  </div>
                </form>
              </section>

              <section class="rounded-lg border border-slate-200 bg-white p-4">
                <div class="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 class="m-0 text-base font-bold text-slate-900">Stores</h3>
                    <p class="m-0 mt-1 text-sm text-slate-500">{{ selectedClient ? clientLabel(selectedClient) : 'No client selected' }}</p>
                  </div>
                  <button mat-raised-button color="primary" type="button" [disabled]="!selectedClient" (click)="startNewStore()">
                    <mat-icon>add_business</mat-icon>
                    Add Store
                  </button>
                </div>

                <div class="grid gap-4 xl:grid-cols-[minmax(260px,1fr)_minmax(320px,420px)]">
                  <div>
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>Search stores</mat-label>
                      <input matInput [(ngModel)]="storeSearch" />
                      <mat-icon matSuffix>search</mat-icon>
                    </mat-form-field>

                    <div class="flex max-h-[520px] flex-col gap-2 overflow-auto pr-1">
                      <button
                        type="button"
                        class="pm-store-row"
                        *ngFor="let store of filteredStores; trackBy: trackStore"
                        [class.pm-store-row-active]="selectedStore?.pool_point_store_uuid === store.pool_point_store_uuid"
                        (click)="selectStore(store)"
                      >
                        <span>
                          <strong>{{ storeLabel(store) }}</strong>
                          <small>{{ addressForStore(store) }}</small>
                          <em *ngIf="store.overlapClients?.length">Also under {{ store.overlapClients?.join(', ') }}</em>
                        </span>
                        <span class="pm-row-actions">
                          <button mat-icon-button matTooltip="Open store contact" (click)="openContact(store.pool_point_store_contact_uuid, $event)">
                            <mat-icon>contact_page</mat-icon>
                          </button>
                          <button mat-icon-button color="warn" matTooltip="Delete store" (click)="deleteStore(store, $event)">
                            <mat-icon>delete</mat-icon>
                          </button>
                        </span>
                      </button>

                      <div class="pm-empty" *ngIf="!loadingDetail && filteredStores.length === 0">
                        No stores.
                      </div>
                    </div>
                  </div>

                  <form [formGroup]="storeForm" class="rounded-lg border border-teal-100 bg-teal-50/40 p-4 pm-subform">
                    <div class="flex items-start justify-between gap-3">
                      <h4>{{ storeMode === 'create' ? 'Add Store' : 'Store Details' }}</h4>
                      <button mat-stroked-button type="button" [disabled]="!selectedClient" (click)="startNewStore()">
                        <mat-icon>add</mat-icon>
                        New Store
                      </button>
                    </div>
                    <div class="grid gap-2 text-sm text-slate-600 md:grid-cols-2">
                      <div class="rounded border border-slate-200 bg-white px-3 py-2">
                        <strong class="block text-xs uppercase text-slate-400">Pool</strong>
                        {{ poolLabel(selectedPool) }}
                      </div>
                      <div class="rounded border border-slate-200 bg-white px-3 py-2">
                        <strong class="block text-xs uppercase text-slate-400">Client</strong>
                        {{ selectedClient ? clientLabel(selectedClient) : 'Select a client' }}
                      </div>
                    </div>
                    <div class="grid gap-3 md:grid-cols-2">
                      <mat-form-field appearance="outline">
                        <mat-label>Store number</mat-label>
                        <input matInput formControlName="pool_point_store_number" />
                      </mat-form-field>
                      <mat-form-field appearance="outline">
                        <mat-label>Client</mat-label>
                        <mat-select formControlName="pool_point_client_uuid">
                          <mat-option *ngFor="let client of clients; trackBy: trackClient" [value]="client.pool_point_client_uuid">
                            {{ clientLabel(client) }}
                          </mat-option>
                        </mat-select>
                      </mat-form-field>
                    </div>
                    <mat-form-field appearance="outline">
                      <mat-label>Store contact</mat-label>
                      <input matInput [formControl]="storeContactSearch" [matAutocomplete]="storeContactAuto" />
                      <mat-icon matSuffix>person_search</mat-icon>
                      <mat-autocomplete #storeContactAuto="matAutocomplete" [displayWith]="displayContactOption" (optionSelected)="onStoreContactSelected($event.option.value)">
                        <mat-option *ngFor="let contact of storeContactOptions$ | async" [value]="contact">
                          <span>{{ contactLabel(contact) }}</span>
                          <small>{{ contactMeta(contact) }}</small>
                        </mat-option>
                      </mat-autocomplete>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Store address</mat-label>
                      <mat-select formControlName="pool_point_store_address_uuid">
                        <mat-option value="">No address</mat-option>
                        <mat-option *ngFor="let address of storeAddresses; trackBy: trackAddress" [value]="address.contact_address_uuid">
                          {{ addressLabel(address) }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Store contact UUID</mat-label>
                      <input matInput formControlName="pool_point_store_contact_uuid" />
                    </mat-form-field>
                    <div class="pm-actions-row">
                      <button mat-raised-button color="primary" type="button" (click)="saveStore()" [disabled]="!selectedPool || !selectedClient || savingStore">
                        <mat-icon>save</mat-icon>
                        {{ storeMode === 'create' ? 'Add Store' : 'Save Store' }}
                      </button>
                    </div>
                  </form>
                </div>
              </section>
            </div>
          </section>
        </main>
      </mat-tab>

      <mat-tab>
        <ng-template mat-tab-label>
          <span class="pm-tab-label">
            <span class="pm-tab-icon bg-emerald-50 text-emerald-700">
              <mat-icon>warehouse</mat-icon>
            </span>
            <span class="pm-tab-text">
              <strong>Distribution Centers</strong>
              <small>{{ distributionCenters.length }} configured</small>
            </span>
          </span>
        </ng-template>
        <section class="grid gap-4 p-4 xl:grid-cols-[minmax(360px,1fr)_minmax(320px,460px)]">
          <div class="rounded-lg border border-slate-200 bg-white p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 class="m-0 text-lg font-bold text-slate-900">Distribution Centers</h2>
                <p class="m-0 mt-1 text-sm text-slate-500">{{ filteredDistributionCenters.length }} shown</p>
              </div>
              <button mat-icon-button color="primary" matTooltip="Add distribution center" (click)="startNewDistributionCenter()">
                <mat-icon>add_location_alt</mat-icon>
              </button>
            </div>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Search distribution centers</mat-label>
              <input matInput [(ngModel)]="dcSearch" />
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>

            <div class="flex max-h-[calc(100vh-330px)] flex-col gap-2 overflow-auto pr-1">
              <button
                type="button"
                class="pm-store-row"
                *ngFor="let dc of filteredDistributionCenters; trackBy: trackDistributionCenter"
                [class.pm-store-row-active]="selectedDistributionCenter?.customer_contact_distribution_center_uuid === dc.customer_contact_distribution_center_uuid"
                (click)="selectDistributionCenter(dc)"
              >
                <span>
                  <strong>{{ distributionCenterLabel(dc) }}</strong>
                  <small>{{ customerLabel(dc) }}</small>
                  <small>{{ dc.distribution_center_reference || 'No reference' }}</small>
                  <small>{{ addressForDistributionCenter(dc) }}</small>
                </span>
                <span class="pm-row-actions">
                  <button mat-icon-button matTooltip="Open customer contact" (click)="openContact(dc.customer_contact_uuid, $event)">
                    <mat-icon>supervisor_account</mat-icon>
                  </button>
                  <button mat-icon-button matTooltip="Open DC contact" (click)="openContact(dc.distribution_center_contact_uuid, $event)">
                    <mat-icon>contact_page</mat-icon>
                  </button>
                  <button mat-icon-button color="warn" matTooltip="Remove DC" (click)="deleteDistributionCenter(dc, $event)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </span>
              </button>

              <div class="pm-empty" *ngIf="!loadingDistributionCenters && filteredDistributionCenters.length === 0">
                No distribution centers.
              </div>
            </div>
          </div>

          <form [formGroup]="distributionCenterForm" class="rounded-lg border border-teal-100 bg-teal-50/40 p-4 pm-subform">
            <h3 class="m-0 text-lg font-bold text-slate-900">{{ distributionCenterMode === 'create' ? 'Add Distribution Center' : 'Distribution Center Details' }}</h3>
            <mat-form-field appearance="outline">
              <mat-label>Customer contact</mat-label>
              <input matInput [formControl]="customerContactSearch" [matAutocomplete]="customerContactAuto" />
              <mat-icon matSuffix>person_search</mat-icon>
              <mat-autocomplete #customerContactAuto="matAutocomplete" [displayWith]="displayContactOption" (optionSelected)="onCustomerContactSelected($event.option.value)">
                <mat-option *ngFor="let contact of customerContactOptions$ | async" [value]="contact">
                  <span>{{ contactLabel(contact) }}</span>
                  <small>{{ contactMeta(contact) }}</small>
                </mat-option>
              </mat-autocomplete>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Distribution center contact</mat-label>
              <input matInput [formControl]="distributionCenterContactSearch" [matAutocomplete]="dcContactAuto" />
              <mat-icon matSuffix>person_search</mat-icon>
              <mat-autocomplete #dcContactAuto="matAutocomplete" [displayWith]="displayContactOption" (optionSelected)="onDistributionCenterContactSelected($event.option.value)">
                <mat-option *ngFor="let contact of distributionCenterContactOptions$ | async" [value]="contact">
                  <span>{{ contactLabel(contact) }}</span>
                  <small>{{ contactMeta(contact) }}</small>
                </mat-option>
              </mat-autocomplete>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Reference</mat-label>
              <input matInput formControlName="distribution_center_reference" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Customer contact UUID</mat-label>
              <input matInput formControlName="customer_contact_uuid" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Distribution center contact UUID</mat-label>
              <input matInput formControlName="distribution_center_contact_uuid" />
            </mat-form-field>
            <div class="pm-actions-row">
              <button mat-raised-button color="primary" type="button" (click)="saveDistributionCenter()" [disabled]="savingDistributionCenter">
                <mat-icon>save</mat-icon>
                {{ distributionCenterMode === 'create' ? 'Add DC' : 'Save DC' }}
              </button>
              <button mat-stroked-button type="button" (click)="startNewDistributionCenter()">
                <mat-icon>restart_alt</mat-icon>
                Clear
              </button>
            </div>
          </form>
        </section>
      </mat-tab>

      <mat-tab>
        <ng-template mat-tab-label>
          <span class="pm-tab-label">
            <span class="pm-tab-icon bg-amber-50 text-amber-700">
              <mat-icon>local_shipping</mat-icon>
            </span>
            <span class="pm-tab-text">
              <strong>Drop Trailer Carriers</strong>
              <small>{{ carriers.length }} active links</small>
            </span>
          </span>
        </ng-template>
        <section class="grid gap-4 p-4 xl:grid-cols-[minmax(360px,1fr)_minmax(320px,460px)]">
          <div class="rounded-lg border border-slate-200 bg-white p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 class="m-0 text-lg font-bold text-slate-900">Drop Trailer Carriers</h2>
                <p class="m-0 mt-1 text-sm text-slate-500">{{ filteredCarriers.length }} shown</p>
              </div>
              <button mat-icon-button matTooltip="Reload carriers" (click)="loadCarriers()">
                <mat-icon>refresh</mat-icon>
              </button>
            </div>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Search carriers</mat-label>
              <input matInput [(ngModel)]="carrierSearch" />
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>

            <div class="flex max-h-[calc(100vh-330px)] flex-col gap-2 overflow-auto pr-1">
              <button type="button" class="pm-store-row" *ngFor="let carrier of filteredCarriers; trackBy: trackCarrier">
                <span>
                  <strong>{{ carrierLabel(carrier) }}</strong>
                  <small>{{ carrierMeta(carrier) || 'No carrier identifiers' }}</small>
                </span>
                <span class="pm-row-actions">
                  <button mat-icon-button matTooltip="Open carrier contact" (click)="openContact(carrier.contact_uuid, $event)">
                    <mat-icon>contact_page</mat-icon>
                  </button>
                  <button mat-icon-button color="warn" matTooltip="Remove carrier" (click)="removeCarrier(carrier, $event)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </span>
              </button>

              <div class="pm-empty" *ngIf="!loadingCarriers && filteredCarriers.length === 0">
                No drop trailer carriers.
              </div>
            </div>
          </div>

          <form [formGroup]="carrierForm" class="rounded-lg border border-teal-100 bg-teal-50/40 p-4 pm-subform">
            <h3 class="m-0 text-lg font-bold text-slate-900">Add Drop Trailer Carrier</h3>
            <mat-form-field appearance="outline">
              <mat-label>Carrier contact</mat-label>
              <input matInput [formControl]="carrierContactSearch" [matAutocomplete]="carrierContactAuto" />
              <mat-icon matSuffix>local_shipping</mat-icon>
              <mat-autocomplete #carrierContactAuto="matAutocomplete" [displayWith]="displayContactOption" (optionSelected)="onCarrierContactSelected($event.option.value)">
                <mat-option *ngFor="let contact of carrierContactOptions$ | async" [value]="contact">
                  <span>{{ contactLabel(contact) }}</span>
                  <small>{{ contactMeta(contact) }}</small>
                </mat-option>
              </mat-autocomplete>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Carrier contact UUID</mat-label>
              <input matInput formControlName="contact_uuid" />
            </mat-form-field>
            <div class="pm-actions-row">
              <button mat-raised-button color="primary" type="button" (click)="addCarrier()" [disabled]="savingCarrier">
                <mat-icon>add</mat-icon>
                Add Carrier
              </button>
            </div>
          </form>
        </section>
      </mat-tab>
    </mat-tab-group>

    <ng-template #noPoolSelected>
      <section class="pm-panel pm-detail pm-empty-state">
        <mat-icon>route</mat-icon>
        <h2>No pool selected</h2>
      </section>
    </ng-template>
  </div>
</app-menu>
